import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { UserRole } from '@feathr/blackbox';
import type { ITableWrapperProps } from '@feathr/components';
import { TanStackTable } from '@feathr/components';
import type { TCollectionTableSort } from '@feathr/extender/hooks';
import { useCollectionTable } from '@feathr/extender/hooks';
import { useAccount, useStore } from '@feathr/extender/state';
import type { IObject } from '@feathr/rachis';

import rolesColumns from './rolesColumns';

interface IRolesTableProps {
  filters?: IObject;
  filterElements?: ITableWrapperProps['filters'];
  filterLabel?: ReactNode;
}

const DEFAULT_PAGE_SIZE = 10;
// Admin and User have the lowest ids, so they lead the table.
const INITIAL_SORT: TCollectionTableSort = [{ id: 'id' }];

function RolesTable({
  filters,
  filterElements,
  filterLabel,
}: Readonly<IRolesTableProps>): JSX.Element {
  const { UserRoles } = useStore();
  const { t } = useTranslation();
  const account = useAccount();

  const tableProps = useCollectionTable<UserRole>(UserRoles, {
    defaultParams: { filters },
    initialPageSize: DEFAULT_PAGE_SIZE,
    initialSort: INITIAL_SORT,
  });

  const columns = useMemo(() => rolesColumns(t, account), [t, account]);

  return (
    <TanStackTable<UserRole>
      {...tableProps}
      columns={columns}
      filterElements={filterElements}
      filterLabel={filterLabel}
      noDataText={t('No roles were found.')}
    />
  );
}

export default observer(RolesTable);
